import React from 'react';

import { CLARIFICATION_COPY } from '../../../../../../../../store/dashboard/askAica/constants';
import {
    formatClarificationNoteLines,
    isClarificationRoundSettled,
} from '../../../../../../../../store/dashboard/askAica/clarificationRound';
import { ClarificationCard } from '../../../../../../../../store/dashboard/askAica/clarificationTypes';

import styles from './ClarificationNote.module.scss';

interface ClarificationNoteProps {
    card: ClarificationCard;
    /** Lets MessagesContainer's per-message Copy reach this element, like CitationAnswer's. */
    registerContentRef?: (el: HTMLElement | null) => void;
}

interface ClarificationPendingLineProps {
    /** True while the docked card above the composer is still waiting on the user. */
    awaitingUser: boolean;
    registerContentRef?: (el: HTMLElement | null) => void;
}

interface ClarificationTranscriptEntryProps {
    card: ClarificationCard;
    /** Share viewers cannot answer, so they always get the read-only note. */
    isViewOnly: boolean;
    /** True while the docked card above the composer is still waiting on the user. */
    awaitingUser: boolean;
    registerContentRef?: (el: HTMLElement | null) => void;
}

/** Read-only Q&A record of a round in the transcript: each question and how it ended. A pending
 * round (only reachable by a share viewer, who cannot answer) reads "awaiting answer". */
export const ClarificationNote: React.FC<ClarificationNoteProps> = ({ card, registerContentRef }) => (
    <div className={styles.ClarificationNote} data-content-role="assistant" ref={registerContentRef} data-testid="clarification-note">
        {formatClarificationNoteLines(card).map((line) => (
            <div key={line.id} className={styles.Line} data-testid="clarification-note-line">
                <span className={styles.Question}>{line.question}</span>
                <span className={styles.Answer}>
                    {line.answer !== null && <>{line.answer} </>}
                    <span className={styles.Qualifier}>{line.answer !== null ? `(${line.qualifier})` : line.qualifier}</span>
                </span>
            </div>
        ))}
    </div>
);

/** The slim transcript marker for a round whose card is docked (or whose answer is in flight). */
export const ClarificationPendingLine: React.FC<ClarificationPendingLineProps> = ({ awaitingUser, registerContentRef }) => (
    <p className={styles.PendingLine} data-content-role="assistant" ref={registerContentRef} data-testid="clarification-pending-line">
        {awaitingUser ? CLARIFICATION_COPY.PENDING_LINE : CLARIFICATION_COPY.SENT_LINE}
    </p>
);

/** What a round looks like in the transcript: the note once settled (or for a viewer), else the slim line. */
export const ClarificationTranscriptEntry: React.FC<ClarificationTranscriptEntryProps> = ({
    card,
    isViewOnly,
    awaitingUser,
    registerContentRef,
}) =>
    isViewOnly || isClarificationRoundSettled(card.questions_state) ? (
        <ClarificationNote card={card} registerContentRef={registerContentRef} />
    ) : (
        <ClarificationPendingLine awaitingUser={awaitingUser} registerContentRef={registerContentRef} />
    );
